"use client";

import { Button } from "@dafiti/closet-ui";
import { Filter, Trash } from "@dafiti/closet-ui/icons";
import { useTranslations } from "next-intl";

import { useFilterActions } from "../../../hooks/useFilterActions.hook";
import { useFiltersQuery } from "../../../hooks/useFiltersQuery.hook";
import { useLiveUpdate } from "../../../hooks/useLiveUpdate.hook";
import { useFiltersStore } from "../../../stores/filters.store";
import type { Facet } from "../../../types/filters.type";
import { FilteredItemsSection } from "../FilteredItemsSection";
import { FilterList } from "../FilterList";

interface FilterRootScreenProps {
  onNavigate: (facet: Facet) => void;
  onApply: () => void;
}

export function FilterRootScreen({
  onNavigate,
  onApply,
}: FilterRootScreenProps) {
  const t = useTranslations("catalog");
  const { query } = useFiltersQuery();
  const { clearDraftFilters, applyFilters } = useFilterActions();
  const { triggerLiveUpdate } = useLiveUpdate();
  const draftTotal = useFiltersStore((state) => state.draftTotal);
  const totalProducts = draftTotal ?? query.data?.pagination?.total ?? 0;

  const handleClear = () => {
    clearDraftFilters();
    triggerLiveUpdate();
  };

  const handleApply = () => {
    applyFilters();
    onApply();
  };

  return (
    <div
      className="flex flex-1 flex-col min-h-0"
      data-testid="filter-root-screen"
    >
      <div className="flex-1 overflow-y-auto min-h-0">
        <FilterList onNavigate={onNavigate} />
      </div>

      <div
        className="flex flex-col gap-4 px-4 pt-4 pb-6 lg:px-4 lg:py-6 shadow-[0_-4px_8px_0_rgba(0,0,0,0.08)] -mx-6"
        data-testid="filter-root-footer"
      >
        <FilteredItemsSection />

        <span
          className="text-body-3 font-medium text-gray-400"
          data-testid="filter-result-count"
        >
          {t("results.resultsCount", { count: totalProducts })}
        </span>

        <div className="flex gap-3xs">
          <Button
            variant="secondary"
            size="lg"
            className="flex-1"
            onClick={handleClear}
            aria-label={t("actions.clearFilters")}
            data-testid="filter-clear-button"
          >
            <div className="w-6 h-6 flex items-center justify-center mr-1">
              <Trash aria-hidden="true" className="size-4" />
            </div>
            {t("actions.clear")}
          </Button>

          <Button
            size="lg"
            className="flex-1"
            onClick={handleApply}
            aria-label={t("actions.filter")}
            data-testid="filter-apply-button"
          >
            <div className="w-6 h-6 flex items-center justify-center mr-1">
              <Filter aria-hidden="true" className="size-4" />
            </div>
            {t("actions.filter")}
          </Button>
        </div>
      </div>
    </div>
  );
}
